import React from "react";
import TableBase from "./Base";
import moment from "moment";
import { Link } from "react-router-dom";

import { DATE_FORMAT_FRONTEND, COLOR_DEFAULT } from "../../constants";
import markerIcons from "../Markers/markerIcons";
import { getTranslation } from "../../utils/helper";
import { Space, Tag } from "antd";

const OrdersRoutePlanTable = ({
  scrollHeight,
  actionWidth,
  withAction,
  routes,
  canEdit,
  setting,
  routePlanType,
  ...props
}) => {
  const defaultColor = COLOR_DEFAULT;
  const isPDP = routePlanType === "PDP";
  const formatRoutes = routes.reduce(
    (object, r) => ({
      ...object,
      [r.driver._id]: r.steps?.filter(s => s.type !== "DRIVER"),
    }),
    {},
  );

  formatRoutes["UNSCHEDULED"] = props.dataSource
    .filter(o => (canEdit ? ["UNSCHEDULED", "UNASSIGNED"].includes(o.status) : true))
    .reduce((object, r) => {
      return isPDP
        ? object.concat([
            { type: "PICKUP", orderId: r._id },
            { type: "DELIVERY", orderId: r._id },
          ])
        : object.concat([{ type: "DELIVERY", orderId: r._id }]);
    }, []);

  const getDriverId = order => {
    if (!canEdit) return "UNSCHEDULED";
    return ["UNSCHEDULED", "UNASSIGNED"].includes(order.status) ? "UNSCHEDULED" : order.driver?._id;
  };

  let COLUMNS = [
    {
      title: getTranslation(setting, "Orders", "Order ID"),
      dataIndex: "orderCode",
      width: 200,
      ellipsis: true,
      fixed: true,
      render(value, record) {
        const pickupIndex = formatRoutes[getDriverId(record)]?.findIndex(
          s => s.type === "PICKUP" && s.orderId === record._id,
        );
        const deliveryIndex = formatRoutes[getDriverId(record)]?.findIndex(
          s => s.type === "DELIVERY" && s.orderId === record._id,
        );
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {isPDP && (
              <span
                style={{
                  border: `1px solid #${record.driver?.color || defaultColor}`,
                  color: `#${record.driver?.color || defaultColor}`,
                  textAlign: "center",
                  minWidth: "22px",
                  minHeight: "22px",
                  lineHeight: "20px",
                  borderRadius: "50%",
                  fontSize: "11px",
                  letterSpacing: -1,
                  paddingRight: 1,
                }}>
                {pickupIndex + 1}
              </span>
            )}
            <span
              style={{
                border: `1px solid #${record.driver?.color || defaultColor}`,
                color: `#${record.driver?.color || defaultColor}`,
                textAlign: "center",
                minWidth: "22px",
                minHeight: "22px",
                lineHeight: "20px",
                borderRadius: 4,
                fontSize: "11px",
                marginLeft: "6px",
                letterSpacing: -1,
                paddingRight: 1,
              }}>
              {deliveryIndex + 1}
            </span>
            <Link to={`/orders/${record._id}`} target="_blank" style={{ paddingLeft: "12px" }}>
              {record.orderRef || record.orderCode}
            </Link>
          </div>
        );
      },
    },
    {
      title: getTranslation(setting, "Orders", "Date"),
      dataIndex: "deliveryDate",
      width: 180,
      ellipsis: true,
      render(value) {
        return value ? moment(value).utc().locale(setting.systemLanguage).format(DATE_FORMAT_FRONTEND) : "-";
      },
    },
    {
      title: getTranslation(setting, "Orders", "Driver"),
      dataIndex: "driver",
      width: 150,
      ellipsis: true,
      render(driver, record) {
        if (!record.driver || record.status === "unassigned") return <></>;
        return (
          <span>
            <img
              src={markerIcons["liveDriver"][driver.color || defaultColor]}
              alt="truck"
              style={{ height: "24px", paddingRight: "5px" }}
            />
            {record.driver?.name}
          </span>
        );
      },
    },
    {
      title: getTranslation(setting, "Orders", "Pickup Name"),
      dataIndex: "pickupName",
      width: 150,
      ellipsis: true,
    },
    {
      title: getTranslation(setting, "Orders", "Pickup Address"),
      dataIndex: "pickupAddress",
      width: 250,
      ellipsis: true,
    },
    {
      title: getTranslation(setting, "Orders", "Pickup Phone"),
      dataIndex: "pickupPhone",
      width: 150,
      ellipsis: true,
    },
    {
      title: getTranslation(setting, "Orders", "Pickup Time Window"),
      dataIndex: "pickupTimeWindow",
      width: 200,
      ellipsis: true,
      render(_value, record) {
        return (record.timeStart || "") + " - " + (record.timeEnd || "");
      },
    },
    {
      title: getTranslation(setting, "Orders", "Delivery Name"),
      dataIndex: "deliveryName",
      width: 150,
      ellipsis: true,
    },
    {
      title: getTranslation(setting, "Orders", "Delivery Address"),
      dataIndex: "deliveryAddress",
      width: 250,
      ellipsis: true,
    },
    {
      title: getTranslation(setting, "Orders", "Delivery Phone"),
      dataIndex: "deliveryPhone",
      width: 150,
      ellipsis: true,
    },
    {
      title: getTranslation(setting, "Orders", "Delivery Time Window"),
      dataIndex: "deliveryTimeWindow",
      width: 200,
      ellipsis: true,
      render(_value, record) {
        return (record.deliveryTimeStart || "") + " - " + (record.deliveryTimeEnd || "");
      },
    },
    {
      title: getTranslation(setting, "Orders", "User Group"),
      dataIndex: ["group", "name"],
      width: 150,
      ellipsis: true,
    },
    {
      title: getTranslation(setting, "Orders", "Load"),
      dataIndex: "load",
      width: 100,
      ellipsis: true,
    },
    {
      title: getTranslation(setting, "Orders", "Types"),
      dataIndex: "requiredSkills",
      width: 150,
      render(skills) {
        return (
          <Space size={[0, 8]} wrap>
            {skills?.map((skill, i) => (
              <Tag key={i}>{skill}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: getTranslation(setting, "Orders", "Status"),
      dataIndex: "status",
      width: 140,
      fixed: "right",
      render(status) {
        return (
          <span className={"status orderStatus " + status.toLowerCase()}>
            {getTranslation(setting, "Orders", status)}
          </span>
        );
      },
    },
  ];

  if (withAction) {
    COLUMNS.push({
      key: "action",
      width: actionWidth || 100,
      fixed: "right",
      render(record) {
        return withAction(record);
      },
    });
  }

  if (routePlanType !== "PDP") {
    COLUMNS = COLUMNS.filter(
      i => !["pickupName", "pickupAddress", "pickupPhone", "pickupTimeWindow"].includes(i.dataIndex),
    );
  }

  if (scrollHeight) {
    return <TableBase columns={COLUMNS} scroll={{ x: 991, y: scrollHeight }} {...props} />;
  } else {
    return <TableBase columns={COLUMNS} scroll={{ x: 991 }} {...props} />;
  }
};

export default OrdersRoutePlanTable;
